import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchQuarterlyResults,
  fetchBalanceSheet,
  fetchPeSnapshot,
  fetchCompanyIndices,
} from '../services/indicesService';
import { fetchNews } from '../services/newsService';
import SentimentBadge from '../components/shared/SentimentBadge';
import AlertToggle from '../components/shared/AlertToggle';
import { useAuth } from '../context/AuthContext';
import { fetchEvents } from '../services/eventsService';
import { fetchSebiActivity } from '../services/sebiService';
import { fetchCompanySectors } from '../services/sectorService';
import NewsList from '../components/market/NewsList';
import SentimentWindowList from '../components/market/SentimentWindowList';
import EventsList from '../components/market/EventsList';
import { useWatchlist } from '../context/WatchlistContext';
import { useEntitlements, FEATURES } from '../context/EntitlementsContext';
import Locked from '../components/shared/Locked';
import { SECTOR_DESCRIPTIONS, INDEX_DESCRIPTIONS, FINANCIALS_DESCRIPTIONS, COMPANY_DESCRIPTIONS } from '../constants/marketDescriptions';
import './CompanyDetailPage.css';

function fmt(val) {
  if (val == null) return '—';
  return Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtCr(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (Math.abs(n) >= 1e7) return '₹' + (n / 1e7).toFixed(2) + ' Cr';
  if (Math.abs(n) >= 1e5) return '₹' + (n / 1e5).toFixed(2) + ' L';
  return '₹' + n.toLocaleString('en-IN');
}

function fmtPe(val) {
  if (val == null) return null;
  const n = Number(val);
  if (n <= 0) return null;
  return n.toFixed(2) + 'x';
}

/**
 * Tabs in the News & Events section, in render order.
 *
 * Doubles as the whitelist for the `?tab=` parameter. Validating against it matters because the
 * value arrives from the URL, where anyone can type anything: an unrecognised tab would otherwise
 * render a section with no content and no way to tell why.
 */
const INSIGHTS_TABS = ['news', 'important', 'events', 'regulatory', 'sentiments'];
const DEFAULT_INSIGHTS_TAB = 'news';

/**
 * Tooltip for the badge beside the company name.
 *
 * Covers the whole pill, not just the badge, so hovering the "Latest News" caption explains itself
 * too. The badge carries a second, more specific tooltip with the article's actual date — the
 * caption says what kind of number this is, the badge says which article produced it.
 */
const LATEST_NEWS_TOOLTIP =
  'Sentiment of the single most recent news article for this company — not an average. '
  + 'It may be from today or from weeks ago; hover the badge itself to see its date. '
  + 'For averages over a period, see the Sentiments tab below.';

/**
 * Which plan each tab belongs to.
 *
 * Latest News is open to everyone: a company page that says nothing to a free reader is not a
 * teaser, it is a dead end. The three middle tabs come with Basic and Sentiments with Advanced -
 * the same split the pricing page describes.
 */
const TAB_FEATURE = {
  news:       null,
  important:  FEATURES.COMPANY_NEWS_TABS,
  events:     FEATURES.COMPANY_NEWS_TABS,
  regulatory: FEATURES.COMPANY_NEWS_TABS,
  sentiments: FEATURES.SENTIMENTS,
};

const TAB_LOCK_COPY = {
  [FEATURES.COMPANY_NEWS_TABS]: {
    title: 'Important news, events and filings',
    note: 'The headlines that matter sorted out from the rest, the company event calendar, and the '
        + 'SEBI filings. Included from the Basic plan upwards.',
  },
  [FEATURES.SENTIMENTS]: {
    title: 'News sentiment',
    note: 'How this company reads in the news, today and averaged over six windows up to a quarter '
        + '- the scores behind the watchlist columns and the Extremes boards. Part of the Advanced '
        + 'plan.',
  },
};

const CompanyDetailPage = () => {
  const { isLoggedIn } = useAuth();
  const { can } = useEntitlements();
  const { symbol } = useParams();
  const navigate   = useNavigate();
  const { entries, activeId, addCompany, isActionLoading } = useWatchlist();
  const isInWatchlist = entries.some(e => e.companyCode === symbol?.toUpperCase());

  const [loading,     setLoading]     = useState(true);
  const [error]                       = useState(null);

  const [quarterly,     setQuarterly]     = useState([]);
  const [balSheet,      setBalSheet]      = useState([]);
  const [pe,            setPe]            = useState(null);
  const [fundLoading,   setFundLoading]   = useState(true);
  const [activeTab,     setActiveTab]     = useState('quarterly');

  const [news,          setNews]          = useState([]);
  const [sentiment,     setSentiment]     = useState(null);
  const [sentimentWindows, setSentimentWindows] = useState([]);
  const [importantNews, setImportantNews] = useState([]);
  const [events,        setEvents]        = useState([]);
  const [sebi,          setSebi]          = useState(null);
  // The tab lives in the URL rather than in component state alone, so that /company/X?tab=sentiments
  // is a real address: it survives a refresh, can be bookmarked, and can be shared. Router state
  // would have been less code but is held in memory only — landing on the Sentiments tab and then
  // pressing F5 would silently drop you back on Latest News, which reads as a bug.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const initialTab = INSIGHTS_TABS.includes(requestedTab) ? requestedTab : DEFAULT_INSIGHTS_TAB;

  const [insightsTab, setInsightsTab] = useState(initialTab);
  const insightsRef = useRef(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [nse500Sectors, setNse500Sectors] = useState([]);
  const [companyIndices, setCompanyIndices] = useState([]);

  useEffect(() => {
    fetchCompanySectors(symbol).then(setNse500Sectors).catch(() => {});
    fetchCompanyIndices(symbol).then(setCompanyIndices).catch(() => {});

    Promise.allSettled([
      fetchQuarterlyResults(symbol),
      fetchBalanceSheet(symbol),
      fetchPeSnapshot(symbol),
    ]).then(([qRes, bRes, pRes]) => {
      if (qRes.status === 'fulfilled') setQuarterly(qRes.value || []);
      if (bRes.status === 'fulfilled') setBalSheet(bRes.value || []);
      if (pRes.status === 'fulfilled') setPe(pRes.value);
    }).finally(() => setFundLoading(false));

    Promise.allSettled([
      fetchNews(symbol),
      fetchEvents(symbol),
      fetchSebiActivity(symbol),
    ]).then(([nRes, eRes, sRes]) => {
      if (nRes.status === 'fulfilled') {
        setNews(nRes.value?.news ?? []);
        setImportantNews(nRes.value?.importantNews ?? []);
        // latestSentiment rides along on the existing /api/news response, so the badge costs no
        // extra request. It is the single most recent article rather than an average: an average
        // needs an expiry rule to avoid reading as current forever on a quiet company, whereas
        // this claims only to be the last thing that happened — and carries its date so the
        // tooltip can say when that was.
        setSentiment(nRes.value?.latestSentiment ?? null);
        // Same for the Sentiments tab breakdown: the backend already has the row loaded, so the
        // six windows cost no extra request and no second database read when the tab is opened.
        setSentimentWindows(nRes.value?.sentimentWindows ?? []);
      }
      if (eRes.status === 'fulfilled') setEvents(eRes.value?.events ?? []);
      if (sRes.status === 'fulfilled') setSebi(sRes.value);
    }).finally(() => setInsightsLoading(false));

    setLoading(false);
  }, [symbol]);

  // Arriving with an explicit tab means the user clicked something that promised to take them
  // there. The tabs sit well below the header, badges and index chips, so selecting one without
  // moving the viewport leaves the reader looking at the top of the page, wondering whether the
  // click registered at all.
  //
  // The guard is load-bearing. This effect has to watch `requestedTab`, because selecting a tab
  // writes it to the URL — so without the ref it would fire again on every tab click and scroll
  // the page each time, which is precisely the yanking it exists to avoid. Tracking the symbol
  // rather than a plain boolean is what keeps it working when the user moves from one company to
  // another: React Router reuses this component across route params, so there is no remount to
  // reset anything.
  const arrivalHandledFor = useRef(null);
  useEffect(() => {
    const isArrival = arrivalHandledFor.current !== symbol;
    arrivalHandledFor.current = symbol;

    if (!isArrival) return;
    if (!INSIGHTS_TABS.includes(requestedTab)) return;

    // Needed in addition to the useState initialiser: that runs once for the component's whole
    // lifetime, so it does not fire when only the symbol changes.
    setInsightsTab(requestedTab);

    // Deferred a frame so the section has been laid out; measuring during the same paint gives the
    // wrong offset on a first load.
    const id = window.requestAnimationFrame(() => {
      insightsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [requestedTab, symbol]);

  /**
   * Selects a tab and records it in the URL.
   *
   * `replace` rather than push: a reader flicking between tabs should not have to press Back once
   * per tab to get out of the page. Back stays a way out to the table they came from.
   */
  const selectInsightsTab = (tab) => {
    setInsightsTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === DEFAULT_INSIGHTS_TAB) {
      next.delete('tab');          // keep the plain company URL clean
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next, { replace: true });
  };

  if (loading) return (
    <div className="cdp-container">
      <div className="cdp-loading">Loading…</div>
    </div>
  );

  if (error) return (
    <div className="cdp-container">
      <div className="cdp-top-bar">
        <button className="cdp-back-btn" onClick={() => navigate(-1)}>← Back</button>
      </div>
      <div className="cdp-error">{error}</div>
    </div>
  );

  const peLabel = pe ? fmtPe(pe.trailingPe) : null;

  /** A padlock on a tab the reader cannot open yet. The tab still selects; its panel explains. */
  const tabLock = (tab) => {
    const feature = TAB_FEATURE[tab];
    return feature && !can(feature)
      ? <span className="nf-nav-lock" aria-label="paid feature">&#128274;</span>
      : null;
  };

  /** What the feature the open tab needs, or null when it is open to everyone. */
  const openTabFeature = TAB_FEATURE[insightsTab];
  const tabLocked      = Boolean(openTabFeature) && !can(openTabFeature);

  /**
   * The selected tab's panel, built once.
   *
   * Pulled out of the JSX so the locked case can put this exact content behind the blur instead of
   * a stand-in. All of it arrived in the same request as Latest News, so showing its shape costs
   * nothing and tells the reader what they would be paying for.
   */
  const insightsPanel = insightsTab === 'news' ? (
    news.length === 0
      ? <div className="cdp-fund-empty">No news found for {symbol}.</div>
      : <NewsList news={news} />
  ) : insightsTab === 'important' ? (
    importantNews.length === 0
      ? <div className="cdp-fund-empty">No important news found for {symbol}.</div>
      : <NewsList news={importantNews} />
  ) : insightsTab === 'events' ? (
    events.length === 0
      ? <div className="cdp-fund-empty">No events found for {symbol}.</div>
      : <EventsList events={events} />
  ) : insightsTab === 'sentiments' ? (
    <SentimentWindowList windows={sentimentWindows} symbol={symbol} />
  ) : (
    <SebiActivityPanel sebi={sebi} symbol={symbol} />
  );

  return (
    <div className="cdp-container">
      <div className="cdp-top-bar">
        <button className="cdp-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <button
          className={`cdp-watchlist-btn ${isInWatchlist ? 'cdp-watchlist-btn--added' : ''}`}
          onClick={() => !isInWatchlist && addCompany(symbol?.toUpperCase(), activeId)}
          disabled={isActionLoading || isInWatchlist}
        >
          {isInWatchlist ? '✓ In Watchlist' : isActionLoading ? 'Adding...' : '+ Add to Watchlist'}
        </button>
      </div>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="cdp-hero">
        <div className="cdp-hero-top">
          <div className="cdp-identity">
            <div className="cdp-symbol">{symbol?.toUpperCase()}</div>
          </div>
          {peLabel && <div className="cdp-pe-pill" data-tooltip={COMPANY_DESCRIPTIONS['pe']}>P/E {peLabel}</div>}
          {sentiment && can(FEATURES.SENTIMENTS) && (
            <div className="cdp-sentiment-pill" title={LATEST_NEWS_TOOLTIP}>
              <span className="cdp-sentiment-label">Latest News</span>
              <SentimentBadge sentiment={sentiment} variant="latest" compact showScore />
            </div>
          )}
          {/* Subscribing is deliberately its own act, separate from the watchlist: a watchlist is
              a reading list, while this puts the company in the reader's inbox. */}
          <AlertToggle symbol={symbol?.toUpperCase()} isLoggedIn={isLoggedIn} />
        </div>

        {(nse500Sectors.length > 0 || companyIndices.length > 0) && (
          <div className="cdp-badge-section">
            {nse500Sectors.map(s => (
              <span
                key={s.displayName}
                className="cdp-badge cdp-badge--nifty cdp-badge--clickable"
                data-tooltip={SECTOR_DESCRIPTIONS[s.displayName]}
                onClick={() => navigate(
                  `/market/domestic/sector/${encodeURIComponent(s.displayName)}`,
                  { state: { newsKeyword: s.newsKeyword } }
                )}
              >
                {s.displayName}
              </span>
            ))}
            {companyIndices.map(idx => (
              <span
                key={idx.indexKey}
                className="cdp-badge cdp-badge--index cdp-badge--clickable"
                data-tooltip={INDEX_DESCRIPTIONS[idx.displayName]}
                onClick={() => navigate(
                  `/market/domestic/index/${encodeURIComponent(idx.indexKey)}`,
                  { state: { displayName: idx.displayName } }
                )}
              >
                {idx.displayName}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── News & Events ─────────────────────────────────────────────────── */}
      <div className="cdp-insights" ref={insightsRef}>
        <div className="cdp-fund-tabs">
          <button
            className={`cdp-fund-tab ${insightsTab === 'news' ? 'active' : ''}`}
            onClick={() => selectInsightsTab('news')}
          >
            Latest News
          </button>
          <button
            className={`cdp-fund-tab ${insightsTab === 'important' ? 'active' : ''}`}
            onClick={() => selectInsightsTab('important')}
          >
            Important News{tabLock('important')}
            {/* The count is held back with the tab: "4 important stories" is itself part of what
                the tab is selling. */}
            {can(FEATURES.COMPANY_NEWS_TABS) && importantNews.length > 0 && (
              <span className="cdp-tab-badge">{importantNews.length}</span>
            )}
          </button>
          <button
            className={`cdp-fund-tab ${insightsTab === 'events' ? 'active' : ''}`}
            onClick={() => selectInsightsTab('events')}
          >
            Events{tabLock('events')}
          </button>
          <button
            className={`cdp-fund-tab ${insightsTab === 'regulatory' ? 'active' : ''}`}
            onClick={() => selectInsightsTab('regulatory')}
          >
            Regulatory{tabLock('regulatory')}
          </button>
          <button
            className={`cdp-fund-tab ${insightsTab === 'sentiments' ? 'active' : ''}`}
            onClick={() => selectInsightsTab('sentiments')}
          >
            Sentiments{tabLock('sentiments')}
          </button>
        </div>
        {insightsLoading ? (
          <div className="cdp-fund-loading">Loading…</div>
        ) : tabLocked ? (
          <Locked
            compact
            feature={openTabFeature}
            title={TAB_LOCK_COPY[openTabFeature].title}
            note={TAB_LOCK_COPY[openTabFeature].note}
          >
            {insightsPanel}
          </Locked>
        ) : (
          insightsPanel
        )}
      </div>

      {/* ── Fundamentals ──────────────────────────────────────────────────── */}
      <div className="cdp-fundamentals">
        <div className="cdp-fund-tabs">
          <button
            className={`cdp-fund-tab ${activeTab === 'quarterly' ? 'active' : ''}`}
            onClick={() => setActiveTab('quarterly')}
          >
            Quarterly Results
          </button>
          <button
            className={`cdp-fund-tab ${activeTab === 'balsheet' ? 'active' : ''}`}
            onClick={() => setActiveTab('balsheet')}
          >
            Balance Sheet
          </button>
        </div>

        {fundLoading && <div className="cdp-fund-loading">Loading financials…</div>}

        {!fundLoading && activeTab === 'quarterly' && (
          quarterly.length === 0
            ? <div className="cdp-fund-empty">No quarterly data yet — will populate overnight.</div>
            : (
              <div className="cdp-fund-table-wrap">
                <table className="cdp-fund-table">
                  <thead>
                    <tr>
                      <th>Quarter</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['Revenue']}>Revenue</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['Gross Profit']}>Gross Profit</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['Op. Profit']}>Op. Profit</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['Net Profit']}>Net Profit</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['EPS (₹)']}>EPS (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quarterly.map((q, i) => {
                      const npPositive = q.netProfit != null && Number(q.netProfit) >= 0;
                      return (
                        <tr key={i}>
                          <td className="cdp-fund-quarter">{q.quarter || q.periodEndDate}</td>
                          <td>{fmtCr(q.revenue)}</td>
                          <td>{fmtCr(q.grossProfit)}</td>
                          <td>{fmtCr(q.operatingProfit)}</td>
                          <td className={npPositive ? 'cdp-fund-gain' : 'cdp-fund-loss'}>
                            {fmtCr(q.netProfit)}
                          </td>
                          <td>{q.eps != null ? Number(q.eps).toFixed(2) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
        )}

        {!fundLoading && activeTab === 'balsheet' && (
          balSheet.length === 0
            ? <div className="cdp-fund-empty">No balance sheet data yet — will populate overnight.</div>
            : (
              <div className="cdp-fund-table-wrap">
                <table className="cdp-fund-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['Total Assets']}>Total Assets</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['Total Debt']}>Total Debt</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['Equity']}>Equity</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['Cash']}>Cash</th>
                      <th data-tooltip={FINANCIALS_DESCRIPTIONS['Liabilities']}>Liabilities</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balSheet.map((b, i) => (
                      <tr key={i}>
                        <td className="cdp-fund-quarter">{b.fiscalYear || b.periodEndDate}</td>
                        <td>{fmtCr(b.totalAssets)}</td>
                        <td className="cdp-fund-loss">{fmtCr(b.totalDebt)}</td>
                        <td className="cdp-fund-gain">{fmtCr(b.shareholdersEquity)}</td>
                        <td>{fmtCr(b.cashAndEquivalents)}</td>
                        <td>{fmtCr(b.totalLiabilities)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {balSheet[0] && (
                  <div className="cdp-bs-breakdown">
                    <div className="cdp-bs-title">{balSheet[0].fiscalYear} — Key Figures</div>
                    <div className="cdp-bs-grid">
                      <div className="cdp-bs-item"><span>Current Assets</span><strong>{fmtCr(balSheet[0].currentAssets)}</strong></div>
                      <div className="cdp-bs-item"><span>Fixed Assets</span><strong>{fmtCr(balSheet[0].fixedAssets)}</strong></div>
                      <div className="cdp-bs-item"><span>Investments</span><strong>{fmtCr(balSheet[0].totalInvestments)}</strong></div>
                      <div className="cdp-bs-item"><span>Current Liab.</span><strong>{fmtCr(balSheet[0].currentLiabilities)}</strong></div>
                      <div className="cdp-bs-item"><span>Long-Term Debt</span><strong>{fmtCr(balSheet[0].longTermDebt)}</strong></div>
                      <div className="cdp-bs-item"><span>Retained Earnings</span><strong>{fmtCr(balSheet[0].retainedEarnings)}</strong></div>
                    </div>
                  </div>
                )}
              </div>
            )
        )}
      </div>
    </div>
  );
};

function SebiActivityPanel({ sebi, symbol }) {
  if (!sebi) {
    return <div className="cdp-fund-empty">No SEBI activity data available for {symbol}.</div>;
  }

  const enforcement = sebi.enforcementActions || [];
  const buybacks    = sebi.buybacks || [];

  if (enforcement.length === 0 && buybacks.length === 0) {
    return <div className="cdp-fund-empty">No SEBI regulatory activity found for {symbol}.</div>;
  }

  const eventTypeLabel = (t) => {
    const map = {
      ADJUDICATION_ORDER: 'Adjudication Order',
      DIRECTION: 'Direction',
      RECOVERY_NOTICE: 'Recovery Notice',
      CIRCULAR: 'Circular',
      FILING: 'Filing',
      BUYBACK: 'Buyback Filing',
      OTHER: 'Other',
    };
    return map[t] || t;
  };

  return (
    <div className="cdp-sebi-panel">
      {enforcement.length > 0 && (
        <div className="cdp-sebi-section">
          <div className="cdp-sebi-section-title">Enforcement Actions ({enforcement.length})</div>
          <div className="cdp-sebi-list">
            {enforcement.map((item, i) => (
              <div key={i} className="cdp-sebi-item">
                <div className="cdp-sebi-item-header">
                  <span className="cdp-sebi-badge cdp-sebi-badge--enforcement">
                    {eventTypeLabel(item.eventType)}
                  </span>
                  <span className="cdp-sebi-date">{item.pubDate}</span>
                </div>
                <div className="cdp-sebi-title">
                  {item.sebiUrl ? (
                    <a href={item.sebiUrl} target="_blank" rel="noopener noreferrer" className="cdp-sebi-link">
                      {item.fullTitle}
                    </a>
                  ) : (
                    item.fullTitle
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {buybacks.length > 0 && (
        <div className="cdp-sebi-section">
          <div className="cdp-sebi-section-title">Buyback Filings ({buybacks.length})</div>
          <div className="cdp-sebi-list">
            {buybacks.map((item, i) => (
              <div key={i} className="cdp-sebi-item">
                <div className="cdp-sebi-item-header">
                  <span className="cdp-sebi-badge cdp-sebi-badge--buyback">Buyback</span>
                  <span className="cdp-sebi-date">{item.buybackDate}</span>
                </div>
                <div className="cdp-sebi-title">
                  {item.sebiCompanyName}
                  {item.sebiUrl && (
                    <a href={item.sebiUrl} target="_blank" rel="noopener noreferrer" className="cdp-sebi-link"> — View Filing</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyDetailPage;
